import React, { useState, useEffect, useContext } from 'react';
import downloadIcon from '../../assets/Icon/download_icon.svg';
import { BatchExportQueueContext } from '../../contexts/BatchExportQueueContext';
import * as XLSX from 'xlsx';

const BatchExportQueueTab = () => {
  const { batchExportQueue, setBatchExportQueue } = useContext(BatchExportQueueContext);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [originalData, setOriginalData] = useState([]); // Store original data for restoration

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(batchExportQueue.length / rowsPerPage);

  // Store original data when batchExportQueue changes
  useEffect(() => {
    if (batchExportQueue.length > 0) {
      setOriginalData(batchExportQueue);
    }
  }, [batchExportQueue]);

  // Reset to first page if data changes
  useEffect(() => {
    setPage(1);
  }, [batchExportQueue, rowsPerPage]);

  const paginatedData = batchExportQueue.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Function to handle download
  const handleDownloadExcel = () => {
    if (batchExportQueue.length === 0) {
      alert('No data to export');
      return;
    }
    
    // Prepare data for Excel
    const excelData = batchExportQueue.map(item => {
      const currentPrice = item.part_price || 0;
      const newPrice = item.new_price || 0;
      const percentChange = currentPrice > 0 ? ((newPrice - currentPrice) / currentPrice * 100).toFixed(2) : '0.00';
      
      return {
        'Unique': item.unique || '',
        'Region': item.region || '',
        'Model Name': item.model_name || '',
        'Interchange Number': item.interchange_number || '',
        'Stock Number': item.stock_number || '',
        'Model Year': item.model_year || '',
        'Current Price': currentPrice,
        'New Price': newPrice,
        'Percent Change': percentChange + '%',
        'Stock Status': item.stock_status || ''
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Batch Export Queue');

    // Generate Excel file and download
    XLSX.writeFile(workbook, 'batch_export_queue.xlsx');
  };

  // Function to handle clear
  const handleClearBatchExport = () => {
    setShowClearConfirmation(true);
  };

  const confirmClear = () => {
    if (confirmText.toLowerCase() === 'confirm') {
      setBatchExportQueue([]);
      setShowClearConfirmation(false);
      setConfirmText('');
    }
  };

  const cancelClear = () => {
    setShowClearConfirmation(false);
    setConfirmText('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && confirmText.toLowerCase() === 'confirm') {
      confirmClear();
    }
  };

  // Function to restore data (exposed globally)
  const restoreData = () => {
    if (originalData.length > 0) {
      setBatchExportQueue(originalData);
    }
  };

  useEffect(() => {
    window.restoreBatchExportData = restoreData;
    return () => {
      delete window.restoreBatchExportData;
    };
  }, [originalData]);

  return (
    <div>
      <div className="control_blk_containers filter_blk mb_15 pb_15">
        <h3 className="font_16 fw-semi text_black mb_15">Batch Export Queue</h3>
        <div className="d-flex justify-content-between align-items-center mb_15">
          <div className="d-flex align-items-center">
            <span className="font_14 text_black mr_10">Show</span>
            <select 
              className="form-control" 
              style={{ width: '80px', marginRight: '10px' }}
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            <span className="font_14 text_black">entries</span>
          </div>
        </div>
        <table className="w-100 table table_color">
          <thead>
            <tr>
              <th>Unique</th>
              <th>Region</th>
              <th>Model Name</th>
              <th>Interchange Number</th>
              <th>Stock Number</th>
              <th>Model Year</th>
              <th>Current price</th>
              <th>New Price</th>
              <th>Percent Change</th>
              <th>Stock Stauts</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, idx) => {
                const currentPrice = row.part_price || 0;
                const newPrice = row.new_price || 0;
                const percentChange = currentPrice > 0 ? ((newPrice - currentPrice) / currentPrice * 100).toFixed(2) : '0.00';

                return (
                  <tr key={idx}>
                    <td>{row.unique_id || ''}</td>
                    <td>{row.region || ''}</td>
                    <td>{row.model_name || ''}</td>
                    <td>{row.interchange_number || ''}</td>
                    <td>{row.stock_number || ''}</td>
                    <td>{row.model_year || ''}</td>
                    <td>{currentPrice}</td>
                    <td>{newPrice}</td>
                    <td>{percentChange}%</td>
                    <td>{row.stock_status || ''}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center' }}>No data available</td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt_10" style={{ width: '100%' }}>
            <div>
              <span className="font_14 text_black">
                Page {page} of {totalPages}
              </span>
            </div>
            <div>
              <button
                className="clear_btn filter_btn mr_10"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ minWidth: 100 }}
              >
                Previous
              </button>
              <button
                className="clear_btn filter_btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ minWidth: 100 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="control_blk_containers filter_blk mb_15 pb_15">
        <h3 className="font_16 fw-semi text_black mb_15">Batch Export Actions</h3>
        <div className="d-flex align-items-center">
          <button className="export_btn d-flex align-items-center mr_15" onClick={handleDownloadExcel}>
            <img src={downloadIcon}  alt="Controls" className="mr_5"/>
            <span className="font_14 fw-reg text_black">Download Batch Price Export</span>
          </button>
          <button className="export_btn" onClick={handleClearBatchExport}>
            <span className="font_14 fw-reg text_black">Clear Batch Export</span>
          </button>
                 </div>
       </div>

               {/* Clear Confirmation Popup */}
        {showClearConfirmation && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              textAlign: 'center'
            }}>
              <h4 style={{ marginBottom: '15px' }}>Confirm Clear</h4>
              <p style={{ marginBottom: '15px' }}>Are you sure you want to clear the Batch Export Queue?</p>
              <p style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                Type "confirm" to proceed:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type 'confirm' here"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button 
                  onClick={confirmClear}
                  disabled={confirmText.toLowerCase() !== 'confirm'}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: confirmText.toLowerCase() === 'confirm' ? '#dc3545' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: confirmText.toLowerCase() === 'confirm' ? 'pointer' : 'not-allowed'
                  }}
                >
                  Clear
                </button>
                <button 
                  onClick={cancelClear}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
     </div>
   );
 };
export default BatchExportQueueTab; 